import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, describeApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, AlertTriangle, ChevronDown, Copy, CheckCircle2, History } from "lucide-react";
import { SegmentedRunProgressHardened } from "@/components/SegmentedRunProgressHardened";
import { HardenedRecoveryPanel } from "@/components/HardenedRecoveryPanel";
import { HardenedReplacementPanel } from "@/components/HardenedReplacementPanel";
import { FastRunPanel } from "@/components/FastRunPanel";
import { TechnicalHistory } from "@/components/TechnicalHistory";
import { CanonicalAuthorizePanel } from "@/components/CanonicalAuthorizePanel";

// Controlli di readiness validi SOLO prima dell'avvio: per un run gia'
// avviato non sono errori (presentazione; i gate backend restano intatti).
const PRE_START_ONLY = ["live_transport_not_instantiated", "authorization_not_consumed", "start_invocation_count_zero"];
// Dopo il completamento del run la deriva del codice e' storica (run immutabile):
// informativa, non un controllo fallito. I gate backend restano intatti.
const POST_TERMINAL_INFO = ["all_hashes_match"];

const P172 = "quote_reader_P1.7.2_SEGMENTED_HARDENED_CANDIDATE";

const Copyable = ({ value, testId }) => (
  <span className="inline-flex items-start gap-1 max-w-full">
    <span className="font-mono text-[10px] break-anywhere" data-testid={testId}>{value}</span>
    <button type="button" aria-label="Copia"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => navigator.clipboard?.writeText(value)}>
      <Copy size={11} />
    </button>
  </span>
);

const Section = ({ title, testId, children }) => (
  <details className="border border-border" data-testid={testId}>
    <summary className="touch-target flex items-center justify-between gap-2 px-2 cursor-pointer select-none text-[11px] font-semibold bg-muted list-none">
      <span>{title}</span>
      <ChevronDown size={14} className="shrink-0" />
    </summary>
    <div className="p-2">{children}</div>
  </details>
);

// Cronologia run hardened/legacy: sola consultazione, distinta per run ID
// e versione. Il vecchio pannello P1.7 vive SOLO qui (mai in parallelo).
export const HardenedRunHistory = ({ history, legacyPanel }) => (
  <Section title={`Cronologia run precedenti (${history.length})`}
           testId="hardened-run-history-section">
    <div className="space-y-1 text-[10px]" data-testid="hardened-run-history">
      {history.map((r) => (
        <div key={r.run_id} className="border-b border-border/60 pb-1 min-w-0"
             data-testid={`hardened-history-row-${r.run_id}`}>
          <div className="flex flex-wrap gap-x-2">
            <span className="font-mono break-anywhere">{r.run_id}</span>
            <b>{r.status}</b>
          </div>
          <div className="text-muted-foreground break-anywhere">
            {r.reader_version}{r.legacy_p17 ? " · legacy P1.7" : ""}
            {r.incomplete_reason ? ` · ${r.incomplete_reason}` : ""}
          </div>
        </div>
      ))}
      {history.length === 0 && <div className="text-muted-foreground">Nessun run precedente.</div>}
    </div>
    {legacyPanel && (
      <details className="mt-2 border border-emerald-300" data-testid="legacy-p17-history-section">
        <summary className="touch-target px-2 text-[11px] font-semibold bg-emerald-50 cursor-pointer list-none flex items-center gap-1">
          <History size={12} />Dettaglio run legacy P1.7 (sola consultazione)
        </summary>
        <div className="p-2">{legacyPanel}</div>
      </details>
    )}
  </Section>
);

// P6I — "Anteprima lettura segmentata hardened" (P1.7.2 canonica).
// Un solo tap => una sola /start (lock sincrono useRef prima di ogni
// await); 202 = successo; errori con HTTP/code/messaggio; nessun
// fallback al pannello legacy; nessun "Riprova" su auth consumata.
const CanonicalP172Panel = ({ jobId, legacyPanel, onLoaded, historical, preflight, role }) => {
  const [a, setA] = useState(null);
  const [checked, setChecked] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(null);
  const [startError, setStartError] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const lock = useRef(false);
  const btnRef = useRef(null);
  const cbRef = useRef(null);
  const inpRef = useRef(null);

  const load = useCallback(() => {
    if (!jobId) return;
    api.get(`/reader/jobs/${jobId}/segmented-run-canonical/authorization`)
      .then(({ data }) => { setA(data); onLoaded?.(data); })
      .catch((e) => {
        const d = { authorization_present: false, hardened_present: false,
          load_error: describeApiError(e), run_history: [] };
        setA(d); onLoaded?.(d);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => { load(); }, [load]);
  // stato run in testa al pannello: ricaricato ogni 5 s finche' il run e' in corso
  // (prima restava "RUNNING" fino a un ricaricamento manuale della pagina)
  const running = a?.run_status === "RUNNING" || a?.run_status === "ALL_SEGMENTS_COMPLETED_PENDING_MERGE";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);

  if (!a) return null;
  const history = a.run_history || [];

  if (!a.authorization_present) {
    return (
      <div className="w-full min-w-0 border-2 border-indigo-300 p-2 sm:p-3 space-y-2 break-anywhere"
           data-testid="hardened-reauth-preview">
        <div className="font-bold text-sm flex items-center gap-2">
          <ShieldCheck size={16} className="text-indigo-700 shrink-0" />
          Anteprima lettura segmentata hardened
        </div>
        <div className="text-[11px] text-muted-foreground">
          Versione candidata: <b className="text-foreground">{a.reader_version_candidate || P172}</b>
        </div>
        {a.load_error && (
          <div className="text-[11px] border border-red-300 bg-red-50 p-2 text-red-900" data-testid="hardened-load-error">
            {a.load_error.message}
          </div>
        )}
        {!a.load_error && (
          <CanonicalAuthorizePanel jobId={jobId} preflight={preflight} policy={a.tenant_budget_policy} role={role}
                                   onAuthorized={() => load()} />
        )}
        <div className="text-[10px] text-muted-foreground" data-testid="hardened-no-auth">
          Nessuna autorizzazione hardened attiva per questo documento: la lettura P1.7.2 richiede la decisione Owner (autorizzazione, poi avvio).
        </div>
        <HardenedRunHistory history={history} legacyPanel={legacyPanel} />
        <SegmentedRunProgressHardened jobId={jobId} />
      </div>
    );
  }

  const ready = a.readiness?.ready;
  const PHRASE = a.confirmation_phrase || "";   // frase esatta congelata nell'autorizzazione
  const phraseOk = PHRASE.length > 0 && phrase === PHRASE;
  const isCanonical = a.is_canonical_p172;
  const caseA = a.dependency_mode === "CASE_A";
  const consumed = a.consumed || a.expired || !a.startable;
  const superseded = a.superseded_version;
  const hc = a.historical_costs || {};
  const bf = a.budget_frozen || {};
  const failingAll = Object.entries(a.readiness?.checks || {})
    .filter(([, v]) => !v).map(([k]) => k);
  // start_invocation_count_zero conta anche gli avvii RIFIUTATI (409): il backend lo
  // rivaluta al momento dell'avvio; qui e' informativo finche' l'autorizzazione e' avviabile
  const rejectedInfo = a.startable && failingAll.includes("start_invocation_count_zero");
  const failing = rejectedInfo ? failingAll.filter((k) => k !== "start_invocation_count_zero") : failingAll;
  const readyUi = rejectedInfo ? failing.length === 0 : !!ready;
  const canStart = a.startable && readyUi && checked && phraseOk && !submitting && !started;
  const controlsLocked = submitting || !!started || consumed;
  const runStarted = !!a.consumed && a.run_status && a.run_status !== "AUTHORIZED_AWAITING_OWNER_START";
  const terminalInfo = runStarted && a.run_terminal ? failing.filter((k) => POST_TERMINAL_INFO.includes(k)) : [];
  const realFailing = runStarted ? failing.filter((k) => !PRE_START_ONLY.includes(k) && !terminalInfo.includes(k)) : failing;
  const preStartInfo = runStarted ? failing.filter((k) => PRE_START_ONLY.includes(k)) : [];
  const ownerNeeded = runStarted && (a.run_status === "SEGMENTED_RUN_INCOMPLETE");

  const onStart = async (ev) => {
    ev?.preventDefault?.();
    if (lock.current) return;              // blocco SINCRONO: tap successivi ignorati
    lock.current = true;
    // disabilitazione DOM immediata (stesso tick), indipendente dallo
    // scheduling React: nessun secondo tap puo' produrre una richiesta
    [btnRef.current, cbRef.current, inpRef.current].forEach((el) => { if (el) el.disabled = true; });
    setSubmitting(true);
    setAttempted(true);
    setStartError(null);
    try {
      const res = await api.post(`/reader/jobs/${jobId}/segmented-run-canonical/start`,
        { confirm: true, confirmation_text: phrase });
      if (res.status === 202 || res.status === 200) {
        setStarted(res.data || { accepted: true });
        setA((prev) => prev ? { ...prev, consumed: true, startable: false,
          run_status: "RUNNING", state: "RUNNING" } : prev);
        load();
      } else {
        setStartError({ status: res.status, code: null,
          message: `Risposta inattesa HTTP ${res.status}: il run non risulta avviato.` });
      }
    } catch (e) {
      const d = describeApiError(e);
      setStartError(d);
      if (d.status === 409) {
        setA((prev) => prev ? { ...prev, startable: false } : prev);
        load();
      }
    } finally {
      setSubmitting(false);
      // il lock resta attivo: una singola azione utente = una sola /start
    }
  };

  return (
    <div className="w-full min-w-0 border-2 border-indigo-300 p-2 sm:p-3 space-y-3 max-w-full break-anywhere"
         data-testid="hardened-reauth-preview">
      <div className="space-y-0.5">
        <div className="font-bold text-sm sm:text-base flex items-center gap-2" data-testid="hardened-reauth-title">
          <ShieldCheck size={16} className="text-indigo-700 shrink-0" />
          {isCanonical ? "Anteprima lettura segmentata canonical hardened P1.7.2" : "Anteprima lettura segmentata hardened"}
        </div>
        <div className="text-[11px] text-muted-foreground">
          Versione: <b className="text-foreground break-anywhere"
                        data-testid="hardened-reauth-version">{a.authorization_reader_version}</b>
          {a.is_canonical_p172 && a.builder_version && <span> · builder {a.builder_version}</span>}
        </div>
      </div>

      {runStarted ? (
        <div className={`text-[11px] px-2 py-1.5 border space-y-0.5 ${realFailing.length ? "border-red-300 bg-red-50" : "border-blue-300 bg-blue-50"}`}
             data-testid="hardened-reauth-readiness">
          <div><b>{ownerNeeded ? "Run avviato — intervento Owner richiesto" : (a.run_terminal ? "Run completato" : "Run avviato")}</b>
            {" "}· stato run: <b>{a.run_status}</b>
            {a.run_terminal && <> · costo consuntivo: <b data-testid="hardened-reauth-run-cost">${Number(a.real_cost_confirmed_usd ?? 0).toFixed(4)}</b> (tetto ${a.max_cost_usd})</>}</div>
          {realFailing.length > 0 && (
            <div className="text-red-900" data-testid="hardened-reauth-real-failing">Controlli falliti: {realFailing.join(", ")}</div>
          )}
          {terminalInfo.length > 0 && (
            <div className="text-[10px] text-muted-foreground" data-testid="hardened-reauth-terminal-info">
              Configurazione del codice cambiata DOPO il completamento ({terminalInfo.join(", ")}): il run e' storico e immutabile; una nuova lettura richiede una nuova autorizzazione Owner con i binding correnti.
            </div>
          )}
          {preStartInfo.length > 0 && (
            <div className="text-[10px] text-muted-foreground" data-testid="hardened-reauth-prestart-info">
              Controlli pre-avvio non più applicabili (informativi): {preStartInfo.join(", ")}
            </div>
          )}
        </div>
      ) : (
        <div className={`text-[11px] px-2 py-1.5 border ${readyUi ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}
             data-testid="hardened-reauth-readiness">
          Readiness gate: <b>{readyUi ? "PRONTO" : "NON PRONTO"}</b>
          {" — "}{failing.length ? failing.join(", ") : "tutti i controlli superati"}
          {rejectedInfo && (
            <div className="text-[10px] text-muted-foreground" data-testid="hardened-reauth-rejected-info">
              Avvii rifiutati in precedenza (409, nessun consumo): {a.rejected_start_attempts ?? "n.d."} — il controllo start_invocation_count_zero viene rivalutato dal server al momento dell'avvio.
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
        <div className="min-w-0">Run:{" "}
          <Copyable value={a.logical_reader_run_id} testId="hardened-reauth-run-id" /></div>
        <div>Stato autorizzazione:{" "}
          <b data-testid="hardened-reauth-auth-status">
            {a.consumed ? "CONSUMATA" : (a.expired ? "SCADUTA" : "NON CONSUMATA")}</b></div>
        <div>Stato run: <b data-testid="hardened-reauth-run-status">{a.run_status}</b></div>
        <div>Scadenza:{" "}
          <b data-testid="hardened-reauth-expiry">{(a.expires_at || "").slice(0, 16)}</b></div>
        <div>Tetto assoluto:{" "}
          <b data-testid="hardened-reauth-cap">${a.max_cost_usd}</b>
          {" "}(tipico ${bf.typical_total_usd} / worst ${bf.worst_case_total_usd})</div>
        <div>Max POST: <b data-testid="hardened-reauth-max-calls">{a.max_calls}</b> (una per segmento, nessun retry POST) · Esito: <b data-testid="hardened-reauth-final-state">{a.final_state_forced}</b></div>
        {isCanonical && (
          <div className="sm:col-span-2" data-testid="hardened-reauth-dependency-mode">
            Dipendenza S0→S1–S6: <b>{caseA ? "CASE_A — nessuna dipendenza S0" : (a.dependency_mode || "—")}</b>
            {" "}· Recovery: <b>solo GET</b> con response_id persistita · Invocazioni /start: <b data-testid="hardened-reauth-start-invocations">{a.start_invocation_count ?? 0}</b>
          </div>
        )}
      </div>

      {a.incomplete_reason && (
        <div className="text-[11px] border border-red-300 bg-red-50 p-2 text-red-900 break-anywhere"
             data-testid="hardened-reauth-incomplete-reason">
          Motivo blocco: {a.incomplete_reason}
        </div>
      )}

      <div className="table-scroll border border-border" data-testid="hardened-reauth-segments">
        <table className="w-full text-[10px]">
          <thead><tr className="border-b border-border bg-muted text-left">
            {["Seg", "Stato", "Tipo", "Codice", "Pagine", "Img", "base input hash", "worst $"].map((h) =>
              <th key={h} className="px-1.5 py-1 font-medium whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {a.segments.map((s) => (
              <tr key={s.segment_id} className="border-b border-border">
                <td className="px-1.5 py-1 font-bold">{s.segment_id}</td>
                <td className="px-1.5 py-1 whitespace-nowrap" data-testid={`hardened-seg-state-${s.segment_id}`}>{s.state || "—"}</td>
                <td className="px-1.5 py-1 whitespace-nowrap">{s.kind}</td>
                <td className="px-1.5 py-1 whitespace-nowrap">{s.component_code || "—"}</td>
                <td className="px-1.5 py-1">{(s.pages || []).join(",")}</td>
                <td className="px-1.5 py-1">{s.n_images}</td>
                <td className="px-1.5 py-1 font-mono">{s.input_hash_short}…</td>
                <td className="px-1.5 py-1">{s.worst_case_usd ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {a.ui_notes?.hypotheses_source_note && (
        <div className="text-[10px] text-muted-foreground" data-testid="hardened-ui-note">
          {a.ui_notes.hypotheses_source_note}
        </div>
      )}

      <div className="text-[11px] bg-slate-50 border border-border px-2 py-1.5">
        <b>Condizioni:</b> 1 POST per segmento · max {a.max_calls} POST ·
        nessun retry POST · GET di polling recuperabili · tetto assoluto
        ${a.max_cost_usd} · esito obbligatorio {a.final_state_forced}.
      </div>
      <Section title="Condizioni di sicurezza (dettaglio)" testId="hardened-reauth-security-section">
        <ul className="text-[10px] list-disc pl-4 space-y-0.5 text-muted-foreground">
          <li>Una sola POST di inferenza per segmento, mai ripetuta automaticamente.</li>
          <li>Tutti i controlli locali (piano canonico, hash, codice, PDF, riferimenti, budget) avvengono PRIMA del consumo dell'autorizzazione.</li>
          <li>I GET di polling sulla response_id persistita sono recuperabili dopo errore o riavvio.</li>
          <li>Il budget gate blocca la POST se la proiezione supera ${a.max_cost_usd}.</li>
          <li>Esito finale forzato a {a.final_state_forced} (nessuna promozione automatica).</li>
        </ul>
      </Section>
      <Section title="Dettaglio costi" testId="hardened-reauth-costs-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 text-[11px]" data-testid="hardened-reauth-costs">
          <div>Tipico nuovo run: <b>${bf.typical_total_usd}</b></div>
          <div>Worst case: <b>${bf.worst_case_total_usd}</b> (margine {bf.safety_margin_pct}%)</div>
          <div>Tetto minimo: <b>${bf.minimum_required_cap_usd}</b></div>
          <div>Tetto Owner: <b>${a.max_cost_usd}</b></div>
          <div>Monolitico confermato: <b>${hc.previous_monolithic_confirmed_usd}</b></div>
          {Number(hc.possible_orphan_s0_p6d1_usd) > 0 && (
            <div>Esposizione incerta registrata su questo documento (massimo): <b>${hc.possible_orphan_s0_p6d1_usd}</b></div>
          )}
          {hc.p171_hardened_failed_before_provider_usd !== undefined && Number(hc.p171_hardened_failed_before_provider_usd) > 0 && (
            <div>Run P1.7.1 hardened (fallito prima del provider): <b>${hc.p171_hardened_failed_before_provider_usd}</b></div>
          )}
          {hc.new_run_uncertain_exposure_usd !== undefined && (
            <div>Esposizione incerta nuovo run: <b>${hc.new_run_uncertain_exposure_usd}</b></div>
          )}
          <div className="text-indigo-800">Esposizione massima complessiva (confermato + incerto + tetto): <b>${hc.total_experiment_max_exposure_usd}</b></div>
          {a.run_terminal && (
            <div className="sm:col-span-2 border-t border-border pt-1 mt-1">Costo consuntivo di questo run: <b data-testid="hardened-costs-consuntivo">${Number(a.real_cost_confirmed_usd ?? 0).toFixed(4)}</b> (stima tipica ${bf.typical_total_usd} · tetto ${a.max_cost_usd})</div>
          )}
        </div>
      </Section>
      <Section title="Hash e configurazione congelata" testId="hardened-reauth-hashes-section">
        <div className="space-y-1 text-[10px]" data-testid="hardened-reauth-hashes">
          {Object.entries({ ...(a.frozen_hashes || {}), ...(a.code_bindings || {}) }).map(([k, v]) => (
            <div key={k} className="min-w-0">
              <span className="text-muted-foreground">{k}:</span>{" "}
              <Copyable value={v} />
            </div>
          ))}
        </div>
      </Section>

      {started && (
        <div className="flex items-start gap-1.5 text-[11px] border border-emerald-400 bg-emerald-50 p-2 text-emerald-900"
             data-testid="hardened-start-accepted">
          <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
          <span>Avvio accettato (HTTP 202): run <span className="font-mono">{started.logical_reader_run_id || a.logical_reader_run_id}</span> accodato
            sul worker backend. Autorizzazione consumata: nessun secondo avvio. Seguire lo stato qui sotto.</span>
        </div>
      )}
      {startError && (
        <div className="text-[11px] border border-red-400 bg-red-50 p-2 text-red-900 break-anywhere space-y-0.5"
             data-testid="hardened-start-error">
          <div className="font-bold">Avvio non eseguito{startError.status ? ` — HTTP ${startError.status}` : ""}{startError.code ? ` · ${startError.code}` : ""}</div>
          <div>{startError.message}</div>
          {startError.status === 409 ? (
            <div className="text-[10px]">Nessun nuovo tentativo automatico: l'esito richiede una decisione Owner.</div>
          ) : (
            <div className="text-[10px]">Nessun nuovo tentativo automatico da questa schermata: verificare lo stato dell'autorizzazione (ricaricando la pagina) prima di qualsiasi nuova decisione Owner.</div>
          )}
        </div>
      )}

      {a.startable && !started && !attempted && (
        <div className="border-t border-border pt-2 space-y-2 safe-pb">
          <label className="flex items-start gap-2 text-[11px]">
            <input type="checkbox" ref={cbRef} checked={checked} disabled={controlsLocked}
                   onChange={(e) => setChecked(e.target.checked)}
                   className="mt-0.5 w-5 h-5 shrink-0"
                   data-testid="hardened-owner-checkbox" />
            <span>Confermo come Owner l'avvio del run segmentato hardened
              (una POST per segmento, tetto assoluto ${a.max_cost_usd}, esito {a.final_state_forced}).</span>
          </label>
          <input type="text" ref={inpRef} value={phrase} disabled={controlsLocked}
                 onChange={(e) => setPhrase(e.target.value)}
                 placeholder={`Digita esattamente: ${PHRASE}`}
                 className="w-full min-h-[44px] border border-border px-2 py-2 text-base font-mono"
                 data-testid="hardened-confirmation-input" />
          <Button ref={btnRef} onClick={onStart} disabled={!canStart}
                  data-testid="hardened-start-button"
                  className="w-full min-h-[44px] bg-indigo-700 hover:bg-indigo-800">
            {canStart ? <ShieldCheck size={14} className="mr-1" /> : <Lock size={14} className="mr-1" />}
            {submitting ? "Avvio in corso…" : (isCanonical ? "Avvia lettura canonical hardened" : "Avvia lettura segmentata hardened")}
          </Button>
        </div>
      )}
      {consumed && !started && !attempted && (
        <div className="flex items-start gap-1.5 text-[10px] text-amber-800" data-testid="hardened-reauth-consumed">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <span>{superseded
            ? `Autorizzazione di versione superata (${a.authorization_reader_version}): stato storico, nessun avvio, nessun retry.`
            : "Autorizzazione consumata, scaduta o superata: stato storico, nessun secondo avvio, nessun retry."}</span>
        </div>
      )}

      {!historical && a.reauthorizable && (
        <Section title="Nuova lettura di questo documento (nuova autorizzazione Owner P1.7.2)" testId="hardened-reauthorize-section">
          <div className="text-[10px] text-muted-foreground mb-2">Il risultato del run precedente resta immutabile e in cronologia. Una nuova lettura richiede una nuova autorizzazione (piano canonico e tetto ricalcolati dal preflight corrente) e poi un nuovo avvio con frase esatta.</div>
          <CanonicalAuthorizePanel jobId={jobId} preflight={preflight} policy={a.tenant_budget_policy} role={role}
                                   title="Nuova autorizzazione Owner P1.7.2 (rilettura)" onAuthorized={() => load()} />
        </Section>
      )}

      {!historical && runStarted && isCanonical && <HardenedRecoveryPanel jobId={jobId} />}
      {!historical && <SegmentedRunProgressHardened jobId={jobId} initialSegments={a.segments} />}
      {!historical && <HardenedRunHistory history={history} legacyPanel={legacyPanel} />}
    </div>
  );
};

// P6L — orchestratore della schermata operativa: se esiste un'autorizzazione
// P1.7.3 Fast mostra SOLO il run attivo HARD3 (stato, S0-S6, costo/budget,
// pulsante Owner, errori utili); tutto il resto (P1.7.2/HARD2, recovery
// consumate, legacy P1.7, marker storici, hash) vive nell'unica sezione
// chiusa "Cronologia tecnica e run precedenti".
export const HardenedReauthPreview = ({ jobId, legacyPanel, onLoaded, preflight }) => {
  const { user } = useAuth();
  const role = user?.role;
  const [fast, setFast] = useState(null);
  const loadFast = useCallback(() => {
    if (!jobId) return;
    api.get(`/reader/jobs/${jobId}/segmented-run-fast/authorization`)
      .then(({ data }) => setFast(data))
      .catch((e) => setFast({ fast_authorization_present: false, load_error: describeApiError(e) }));
  }, [jobId]);
  useEffect(() => { loadFast(); }, [loadFast]);
  useEffect(() => {
    if (fast?.fast_authorization_present) {
      onLoaded?.({ hardened_present: true, fast_present: true, reader_version: fast.authorization_reader_version });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fast?.fast_authorization_present, fast?.authorization_reader_version]);

  if (!fast) return null;
  if (!fast.fast_authorization_present) {
    return <CanonicalP172Panel jobId={jobId} legacyPanel={legacyPanel} onLoaded={onLoaded} preflight={preflight} role={role} />;
  }
  return (
    <div className="w-full min-w-0 space-y-3 max-w-full break-anywhere" data-testid="operational-screen">
      <FastRunPanel a={fast} jobId={jobId} onChanged={loadFast} />
      {fast.consumed && <HardenedRecoveryPanel jobId={jobId} />}
      {fast.consumed && <HardenedReplacementPanel jobId={jobId} />}
      <SegmentedRunProgressHardened jobId={jobId} initialSegments={fast.segments}
                                    statusPath="segmented-run-fast/status" />
      <TechnicalHistory jobId={jobId} excludeRunId={fast.logical_reader_run_id}>
        {legacyPanel && (
          <details className="border border-emerald-300" data-testid="legacy-p17-history-section">
            <summary className="touch-target px-2 text-[11px] font-semibold bg-emerald-50 cursor-pointer list-none flex items-center gap-1">
              <History size={12} />Dettaglio run legacy P1.7 (sola consultazione)
            </summary>
            <div className="p-2">{legacyPanel}</div>
          </details>
        )}
      </TechnicalHistory>
    </div>
  );
};
